import React from "react";

import { DateCalendar, DateCalendarProps } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { PickersCalendarHeaderProps, PickersDay, PickersDayProps, LocalizationProvider } from "@mui/x-date-pickers-pro";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import "dayjs/locale/nl";
import { MdChevronRight, MdChevronLeft } from "react-icons/md";
import dayjs, { Dayjs } from "dayjs";

import { IconButton, MenuItem, Select } from "@mui/material";
import { calendarMonths, generateYears } from "./calendarHelpers";
import { Stack } from "@mui/material";


export type WcDateCalendarProps = DateCalendarProps  & {
  handleMonthChange?: (month: Dayjs) => void;
  onTodayButtonPress?: (value: Dayjs | string) => void;
};

export const WcDateCalendar = ({ value, handleMonthChange, onTodayButtonPress, ...props }: WcDateCalendarProps) => {
  const CustomCalendarHeader = (
    customCalendarHeaderProps: PickersCalendarHeaderProps  & {
      todayLabel?: string;
    }
  ) => {
    const { todayLabel, onMonthChange, currentMonth, ...others } = customCalendarHeaderProps;
    const [localMonth, setLocalMonth] = React.useState(currentMonth.month());
    const [localYear, setLocalYear] = React.useState(currentMonth.year());

    React.useEffect(() => {
      setLocalMonth(currentMonth.month());
      setLocalYear(currentMonth.year());
    }, [currentMonth]);

    const yearValuesArray = generateYears();

    const onMonthSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedMonth = dayjs().month(Number(event.target.value));
      setLocalMonth(selectedMonth.month());
      if (onMonthChange) {
        onMonthChange(selectedMonth);
      }
    };

    const onYearSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedYear = dayjs().year(Number(event.target.value));
      setLocalYear(selectedYear.year());
      setLocalMonth(selectedYear.month());
      if (onMonthChange) {
        onMonthChange(selectedYear);
      }
    };

    const onTodayPressed = () => {
      const today = dayjs();
      setLocalYear(today.year());
      setLocalMonth(today.month());
      onTodayButtonPress(dayjs());
    };

    return (
      <Stack
        className={customCalendarHeaderProps.className}
        display={"flex"}
        direction={"row"}
        spacing={2}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Select sx={{ width: 120 }} variant="standard" value={localMonth.toString()} onChange={onMonthSelected}>
          {calendarMonths.map((cal) => (
            <MenuItem key={cal.key} value={cal.value}>
              {cal.key}
            </MenuItem>
          ))}
        </Select>
        <Select variant="standard" sx={{ width: 120 }} value={localYear.toString()} onChange={onYearSelected}>
          {yearValuesArray.map((cal) => (
            <MenuItem key={cal.key} value={cal.value}>
              {cal.key}
            </MenuItem>
          ))}
        </Select>
        <Button onClick={onTodayPressed}>{todayLabel}</Button>
        <Stack direction={"row"}>
          <IconButton
            onClick={() => {
              const newMonth = dayjs(currentMonth).subtract(1, "month");
              setLocalYear(newMonth.year());
              setLocalMonth(newMonth.month());
              onMonthChange(newMonth);
            }}
          >
            <MdChevronLeft />
          </IconButton>
          <IconButton
            onClick={() => {
              const newMonth = dayjs(currentMonth).add(1, "month");
              setLocalYear(newMonth.year());
              setLocalMonth(newMonth.month());
              onMonthChange(newMonth);
            }}
          >
            <MdChevronRight />
          </IconButton>
        </Stack>
      </Stack>
    );
  };

  const CustomHighlightDays = (
    customHighlightDaysProps: PickersDayProps & {
      highlightedDays?: number[];
      highlightedColor?: string;
    }
  ) => {
    const { highlightedDays = [], day, outsideCurrentMonth, highlightedColor, ...other } = customHighlightDaysProps;

    const isSelected = !customHighlightDaysProps.outsideCurrentMonth && highlightedDays.indexOf(customHighlightDaysProps.day.date()) >= 0;
    return (
      <Box key={customHighlightDaysProps.day.toString()}>
        <PickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
          sx={{
            backgroundColor: isSelected ? highlightedColor : undefined,
            fontWeight: isSelected ? "bold" : undefined,
          }}
        />
      </Box>
    );
  };

  return (
    <LocalizationProvider adapterLocale={"nl"} dateAdapter={AdapterDayjs}>
      <DateCalendar
        value={value}
        onMonthChange={handleMonthChange}
        slots={{
          day: CustomHighlightDays,
          calendarHeader: CustomCalendarHeader,
          ...props.slots,
        }}
        slotProps={{
          calendarHeader: {
            //@ts-ignore
            ...props.slotProps?.calendarHeader,
          },
          day: {
            //@ts-ignore
            ...props.slotProps?.highlightedDays,
            //@ts-ignore
            ...props.slotProps?.highlightedColor,
          } as any,
          ...props.slotProps,
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};

// export const WcCustomHighlightDays = ({
//   children,
//   ...props
// }: WcCustomHighlightDaysProps) => {
//   <PickersDay {...props}>{children}</PickersDay>;
// };

export default WcDateCalendar;

// code from main WcDateCalendar
